import type { CatalogCategoryId, CatalogEntry } from "../contracts/catalog";
import type { HomeSlot } from "./game-registry";
import type { ExternalGameLink } from "./rosebud-catalog";

export const LAUNCHER_PAGE_SIZE = 12;
/** Categories is always the first tile; the game grids intentionally omit Create. */
export const LAUNCHER_GAME_FIXED_SLOTS = 1;
/** The category picker additionally places Create in its second tile. */
export const LAUNCHER_CATEGORY_PICKER_FIXED_SLOTS = 2;

export type LauncherSlot =
  | { readonly kind: "create" }
  | { readonly kind: "categories" }
  | { readonly kind: "local"; readonly manifest: Extract<HomeSlot, { kind: "game" }>["manifest"] }
  | { readonly kind: "external"; readonly game: ExternalGameLink }
  | { readonly kind: "catalog-entry"; readonly entry: CatalogEntry }
  | { readonly kind: "category-option"; readonly category: CatalogCategoryId }
  | { readonly kind: "placeholder"; readonly key: string };

export function paginate(slots: readonly LauncherSlot[]): readonly (readonly LauncherSlot[])[] {
  const pageCount = Math.max(1, Math.ceil(slots.length / LAUNCHER_PAGE_SIZE));
  const pages: LauncherSlot[][] = [];

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const page = slots.slice(
      pageIndex * LAUNCHER_PAGE_SIZE,
      (pageIndex + 1) * LAUNCHER_PAGE_SIZE,
    );
    while (page.length < LAUNCHER_PAGE_SIZE) {
      page.push({
        kind: "placeholder",
        key: `placeholder-${pageIndex + 1}-${page.length + 1}`,
      });
    }
    pages.push(page);
  }

  return pages;
}

/**
 * The Categories entry is navigation, not catalog content: keep it in the
 * top-left position on every paginated game page instead of letting it scroll
 * off with the first eleven results.
 */
export function paginateWithLeadingSlot(
  leadingSlot: LauncherSlot,
  slots: readonly LauncherSlot[],
): readonly (readonly LauncherSlot[])[] {
  const pageCapacity = LAUNCHER_PAGE_SIZE - 1;
  const pageCount = Math.max(1, Math.ceil(slots.length / pageCapacity));
  const pages: LauncherSlot[][] = [];

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const page: LauncherSlot[] = [
      leadingSlot,
      ...slots.slice(pageIndex * pageCapacity, (pageIndex + 1) * pageCapacity),
    ];
    while (page.length < LAUNCHER_PAGE_SIZE) {
      page.push({
        kind: "placeholder",
        key: `placeholder-${pageIndex + 1}-${page.length + 1}`,
      });
    }
    pages.push(page);
  }

  return pages;
}

export function composePages(
  localSlots: readonly HomeSlot[],
  catalog: readonly ExternalGameLink[],
): readonly (readonly LauncherSlot[])[] {
  // Source-tier ordering happens before pagination. Never truncate primary
  // CARCADE games to make room for external catalog entries.
  const localGames: LauncherSlot[] = localSlots
    .filter((slot): slot is Extract<HomeSlot, { kind: "game" }> => slot.kind === "game")
    .map(({ manifest }) => ({ kind: "local", manifest }));
  const externalGames: LauncherSlot[] = catalog.map((game) => ({ kind: "external", game }));
  return paginateWithLeadingSlot({ kind: "categories" }, [...localGames, ...externalGames]);
}
